import { useEditor } from '../../state/editorStore';
import { CAPTION_LINE_PRESETS, type CaptionLineStyle } from '@captionflow/caption-engine';
import { FONT_REGISTRY } from '../fontList';
import { WEIGHTS, Row, Seg, Slider, Toggle, hex, nearestWeight, pct, compact } from './controls';

/**
 * Edit style → Lines: give each caption row (line 1, line 2, line 3) its own
 * font / weight / italic / size / colour - editorial stacks like "thin italic
 * over heavy". Writes the style override `lineStyles` (preview + export).
 */
const ROWS = 3;

export function StudioCaptionLines() {
  const lineStyles = useEditor((s) => s.resolvedStyle().lineStyles ?? null);
  const updateStyle = useEditor((s) => s.updateStyle);

  const rows: (CaptionLineStyle | null)[] = Array.from({ length: ROWS }, (_, i) => lineStyles?.[i] ?? null);
  const write = (next: (CaptionLineStyle | null)[] | null) => {
    const trimmed = next ? [...next] : null;
    while (trimmed && trimmed.length && !trimmed[trimmed.length - 1]) trimmed.pop();
    updateStyle({ lineStyles: trimmed && trimmed.length ? trimmed : null } as never);
  };
  const setRow = (i: number, patch: Partial<{ [K in keyof CaptionLineStyle]: CaptionLineStyle[K] | undefined }>) => {
    const next = [...rows];
    const merged = compact({ ...(next[i] ?? {}), ...patch });
    next[i] = Object.keys(merged).length ? merged : null;
    write(next);
  };
  const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const current = (lineStyles ?? []).filter(Boolean).length ? lineStyles : null;

  return (
    <div className="shk-fields">
      <div className="hkp-grid">
        {CAPTION_LINE_PRESETS.map((p) => (
          <button key={p.id} className={same(trim(current), trim(p.rows)) ? 'hkp on' : 'hkp'} title={p.desc} onClick={() => write(p.rows)}>
            <span className="hkp-sample hkp-stack cl-sample">
              {['Line', 'two'].map((w, i) => {
                const r = p.rows?.[i] ?? null;
                return (
                  <span key={w} style={{
                    display: 'block',
                    fontFamily: r?.fontFamily ?? 'Inter',
                    fontWeight: r?.fontWeight ?? 800,
                    fontStyle: r?.italic ? 'italic' : undefined,
                    fontSize: `${Math.round(15 * Math.min(1.4, Math.max(0.6, r?.size ?? 1)))}px`,
                    color: r?.color ?? '#fff',
                    textTransform: 'none',
                  }}>{w}</span>
                );
              })}
            </span>
            <span className="hkp-name">{p.name}</span>
          </button>
        ))}
      </div>

      {rows.map((r, i) => (
        <div key={i} className="cl-row">
          <span className="str-section">Line {i + 1}</span>
          <Row label="Font">
            <select className="ses-select" value={r?.fontFamily ?? ''} onChange={(e) => setRow(i, { fontFamily: e.target.value || undefined })}>
              <option value="">Caption font</option>
              {FONT_REGISTRY.map((f) => <option key={f.family} value={f.family}>{f.displayName}</option>)}
            </select>
          </Row>
          <Row label="Weight">
            <Seg options={[{ v: '', label: '—' }, ...WEIGHTS]} value={r?.fontWeight ? String(nearestWeight(r.fontWeight)) : ''}
              onPick={(v) => setRow(i, { fontWeight: v ? Number(v) : undefined })} />
          </Row>
          <Row label="Italic">
            <Toggle on={Boolean(r?.italic)} onClick={() => setRow(i, { italic: r?.italic ? undefined : true })} />
          </Row>
          <Slider label="Size" min={0.4} max={2} step={0.05} value={r?.size ?? 1}
            onChange={(v) => setRow(i, { size: Math.abs(v - 1) < 0.01 ? undefined : v })} display={pct(r?.size ?? 1)} />
          <Row label="Own color">
            <div className="shk-inline">
              <Toggle on={Boolean(r?.color)} onClick={() => setRow(i, { color: r?.color ? undefined : '#ffd400' })} />
              {r?.color && (
                <input type="color" className="shk-color" value={hex(r.color, '#ffd400')} onChange={(e) => setRow(i, { color: e.target.value })} />
              )}
            </div>
          </Row>
        </div>
      ))}
    </div>
  );
}

function trim(rows: (CaptionLineStyle | null)[] | null | undefined) {
  if (!rows) return null;
  const t = [...rows];
  while (t.length && !t[t.length - 1]) t.pop();
  return t.length ? t : null;
}
